import { createPortal } from 'react-dom';
import type { GenerationAsset } from '../state/useGenerationState';
import type { VideoSettings } from './VideoGenerationSettings';

interface AspectMismatchDialogProps {
  isOpen: boolean;
  videoSettings: VideoSettings;
  selectedAssets: GenerationAsset[];
  onConfirm: () => void;
  onChangeAndConfirm: (target: '16:9' | '9:16') => void;
  onCancel: () => void;
}

const PREVIEW_H = 110;

export function AspectMismatchDialog({
  isOpen,
  videoSettings,
  selectedAssets,
  onConfirm,
  onChangeAndConfirm,
  onCancel,
}: AspectMismatchDialogProps) {
  if (!isOpen) return null;

  // フレームモードの確認モーダルは Veo 系の 16:9 / 9:16 のみ対象。
  // Seedance の他比率 (21:9 / 4:3 等) はそもそも frame モード非対応なので
  // ここでは 9:16 と 16:9 に narrowing する。
  const current: '16:9' | '9:16' = videoSettings.aspectRatio === '9:16' ? '9:16' : '16:9';
  const suggested: '16:9' | '9:16' = current === '16:9' ? '9:16' : '16:9';
  const firstAsset = selectedAssets[0];

  const renderPreview = (outputAspect: '16:9' | '9:16', label: string, onClick: () => void, primary: boolean) => {
    if (!firstAsset?.width || !firstAsset?.height || !firstAsset.thumbnailUrl) return null;
    const sR = firstAsset.width / firstAsset.height;
    const oR = outputAspect === '16:9' ? 16 / 9 : 9 / 16;
    const containerW = Math.round(PREVIEW_H * sR);
    const containerH = PREVIEW_H;
    const frameW = sR < oR ? containerW : Math.round(containerH * oR);
    const frameH = sR < oR ? Math.round(containerW / oR) : containerH;
    const frameLeft = Math.round((containerW - frameW) / 2);
    const frameTop = Math.round((containerH - frameH) / 2);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: 6,
        }}
      >
        <div style={{ position: 'relative', width: containerW, height: containerH, borderRadius: 6, overflow: 'hidden', pointerEvents: 'none' }}>
          <img src={firstAsset.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {frameTop > 0 && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: frameTop, background: 'rgba(0,0,0,0.6)' }} />}
          {frameTop > 0 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: frameTop, background: 'rgba(0,0,0,0.6)' }} />}
          {frameLeft > 0 && <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: frameLeft, background: 'rgba(0,0,0,0.6)' }} />}
          {frameLeft > 0 && <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: frameLeft, background: 'rgba(0,0,0,0.6)' }} />}
          <div style={{
            position: 'absolute',
            left: frameLeft,
            top: frameTop,
            width: frameW,
            height: frameH,
            border: primary ? '2px solid rgba(var(--brand-primary-rgb) / 1)' : '2px solid rgba(255,255,255,0.75)',
            boxSizing: 'border-box',
          }} />
        </div>
        <span style={{ fontSize: 11, color: 'rgba(245,241,230,0.6)' }}>出力 {outputAspect}</span>
        <button
          type="button"
          onClick={onClick}
          style={{
            fontSize: 12,
            color: primary ? 'rgba(var(--brand-primary-rgb) / 1)' : 'rgba(245, 241, 230, 0.9)',
            fontWeight: primary ? 700 : 500,
            textAlign: 'center',
            background: primary ? 'rgba(var(--brand-primary-rgb) / 0.12)' : 'rgba(245, 241, 230, 0.08)',
            border: primary ? '1px solid rgba(var(--brand-primary-rgb) / 0.6)' : '1px solid rgba(245, 241, 230, 0.25)',
            borderRadius: 6,
            padding: '6px 12px',
            cursor: 'pointer',
          }}
        >
          {label}
        </button>
      </div>
    );
  };

  return createPortal(
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, position: 'relative' }}>
        <button
          type="button"
          onClick={onCancel}
          aria-label="キャンセル"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'rgba(245, 241, 230, 0.95)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ✕
        </button>
        <p className="confirm-dialog-message">
          <strong>アスペクト比の不一致</strong>
          <br />
          選択されたフレーム素材のアスペクト比が、動画設定（{current}）と一致していません。
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'flex-start', margin: '12px 0 4px' }}>
          {renderPreview(current, `このまま${current}で生成`, onConfirm, false)}
          {renderPreview(suggested, `${suggested}に変更して生成`, () => onChangeAndConfirm(suggested), true)}
        </div>
      </div>
    </div>,
    document.body,
  );
}
